import {
  AbsoluteFill,
  Audio,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { END_CTA_COPY } from "../shared/EndCta";
import {
  END_CTA_DURATION_SECONDS,
  getEndCtaTiming,
} from "../shared/endCtaTiming";
import { InstrumentIcon } from "./InstrumentIcon";
import { DURATION, ENTRIES } from "./score";
import { EVENTS, beatTime, pitchMidi } from "./scheduler";
import {
  ORBITS,
  PERIOD,
  TAU,
  entryWeight,
  markerVisible,
  position,
  reaction,
  smooth,
} from "./orbit";
export const COPY = {
  brand: "LOOP NIBBLE",
  title: "ORBIT",
  subtitle: "MUSIC BOX",
};
/** SVG keeps the CTA identical in Remotion and the browser-free exporter. */
const OrbitEndCta: React.FC<{ time: number }> = ({ time }) => {
  const start = DURATION - END_CTA_DURATION_SECONDS;
  if (time < start || time >= DURATION) return null;
  const timing = getEndCtaTiming((time - start) * 60, 60);
  return (
    <g
      opacity={timing.opacity}
      transform={`translate(520 ${1590 + timing.translateY}) scale(${timing.scale})`}
    >
      <rect
        x="-390"
        y="-65"
        width="780"
        height="130"
        rx="32"
        fill="#111D30"
        stroke="#34465E"
        strokeWidth="2"
      />
      <g transform={`translate(-310 0) scale(${1 + timing.pulse * 0.16})`}>
        <circle r="39" fill="#FF7398" fillOpacity=".12" />
        <path
          d="M0 22 C-8 15 -27 2 -27 -11 C-27 -29 -7 -31 0 -18 C7 -31 27 -29 27 -11 C27 2 8 15 0 22Z"
          fill="#FF7398"
        />
      </g>
      <text
        x="-246"
        y="14"
        fill="#F8F2E8"
        fontFamily="Arial, sans-serif"
        fontSize="40"
        fontWeight="700"
      >
        {END_CTA_COPY.like}
      </text>
      <g transform={`translate(151 0) scale(${1 + timing.pulse * 0.035})`}>
        <rect x="-181" y="-40" width="362" height="80" rx="20" fill="#74EEFF" />
        <text
          y="13"
          textAnchor="middle"
          fill="#0B1324"
          fontFamily="Arial, sans-serif"
          fontSize="35"
          fontWeight="700"
          letterSpacing="2"
        >
          {END_CTA_COPY.subscribe}
        </text>
      </g>
    </g>
  );
};
/** Pure SVG scene shared by Remotion and the deterministic still exporter. */
export const OrbitArtwork: React.FC<{ time: number }> = ({ time }) => {
  const ending = smooth((time - 32.25) / 1.75);
  const active = ORBITS.filter((o) => time >= beatTime(ENTRIES[o.id]));
  return (
    <svg
      viewBox="0 0 1080 1920"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="bg">
          <stop stopColor="#17283F" />
          <stop offset="1" stopColor="#070B18" />
        </radialGradient>
        <radialGradient id="core">
          <stop stopColor="#FFF9EA" stopOpacity=".9" />
          <stop offset=".22" stopColor="#BAA9FF" stopOpacity=".3" />
          <stop offset="1" stopColor="#9789FF" stopOpacity="0" />
        </radialGradient>
        {ORBITS.map((o) => (
          <radialGradient key={o.id} id={`light-${o.id}`}>
            <stop stopColor={o.color} stopOpacity=".5" />
            <stop offset="1" stopColor={o.color} stopOpacity="0" />
          </radialGradient>
        ))}
      </defs>
      <rect width="1080" height="1920" fill="url(#bg)" />
      <text
        x="540"
        y="269"
        textAnchor="middle"
        fill="#8390A7"
        fontFamily="Arial, sans-serif"
        fontSize="21"
        letterSpacing="8"
      >
        {COPY.brand}
      </text>
      <text
        x="540"
        y="380"
        textAnchor="middle"
        fill="#F8F2E8"
        fontFamily="Arial, sans-serif"
        fontSize="90"
        fontWeight="700"
        letterSpacing="15"
      >
        {COPY.title}
      </text>
      <text
        x="540"
        y="429"
        textAnchor="middle"
        fill="#A3AEC2"
        fontFamily="Arial, sans-serif"
        fontSize="24"
        letterSpacing="12"
      >
        {COPY.subtitle}
      </text>
      <g transform="translate(520 940)">
        <circle r="450" fill="#0B1324" stroke="#202B40" strokeWidth="2" />
        <circle r="427" fill="none" stroke="#2B354A" strokeWidth="1" />
        {Array.from({ length: 64 }, (_, i) => {
          const a = (i * TAU) / 64;
          return (
            <line
              key={i}
              x1={Math.cos(a) * 430}
              y1={Math.sin(a) * 430}
              x2={Math.cos(a) * (i % 4 === 0 ? 441 : 435)}
              y2={Math.sin(a) * (i % 4 === 0 ? 441 : 435)}
              stroke="#687087"
              strokeOpacity={i % 4 === 0 ? 0.5 : 0.22}
              strokeWidth="2"
            />
          );
        })}
        <line
          x1="0"
          x2="0"
          y1="-419"
          y2="-75"
          stroke="#DFE9FC"
          strokeOpacity=".2"
          strokeWidth="2"
        />
        <path
          d="M-12,-452 L0,-436 L12,-452"
          fill="none"
          stroke="#FFF1D8"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {ORBITS.map((orbit, index) => {
          const weight = entryWeight(orbit.id, time);
          if (weight === 0) return null;
          const events = EVENTS.filter((e) => e.circleId === orbit.id);
          const recent = events.filter(
            (e) => time >= e.time && time < e.end + orbit.release,
          );
          const pulse = recent.reduce(
            (m, e) => Math.max(m, reaction(e, time, orbit.release)),
            0,
          );
          const r = orbit.radius;
          const enterTime = beatTime(ENTRIES[orbit.id]);
          const burst =
            orbit.id !== "marimba" &&
            time >= enterTime &&
            time < enterTime + 0.8
              ? 1 - (time - enterTime) / 0.8
              : 0;
          // Scale only before the first attack; marker path is exact at all note onsets.
          return (
            <g
              key={orbit.id}
              opacity={weight}
              transform={`scale(${0.72 + 0.28 * weight})`}
            >
              <circle
                r={r}
                fill="none"
                stroke={orbit.color}
                strokeWidth="22"
                strokeOpacity=".035"
              />
              <circle
                r={r}
                fill="none"
                stroke={orbit.color}
                strokeWidth={3 + pulse * 2}
                strokeOpacity={0.5 + 0.35 * pulse}
              />
              <circle
                r={r + 10 + pulse * (index === 2 ? 15 : 5)}
                fill="none"
                stroke={orbit.color}
                strokeWidth="1"
                strokeOpacity={0.08 + pulse * 0.18}
              />
              <g transform={`rotate(${(time / PERIOD) * 360})`}>
                {Array.from({ length: 16 }, (_, i) => {
                  const a = (i * TAU) / 16;
                  return (
                    <circle
                      key={i}
                      cx={r * Math.cos(a)}
                      cy={r * Math.sin(a)}
                      r={i % 4 === 0 ? 3 : 1.5}
                      fill={orbit.color}
                      opacity=".35"
                    />
                  );
                })}
                <path
                  d={`M0,${-r} A${r},${r} 0 0 1 ${r},0`}
                  fill="none"
                  stroke={orbit.color}
                  strokeWidth="8"
                  strokeOpacity=".12"
                  strokeLinecap="round"
                />
              </g>
              {events
                .filter((e) => markerVisible(e, time))
                .map((e) => {
                  const p = position(e, time, r),
                    until = e.time - time;
                  const opacity =
                    smooth((time - (e.time - PERIOD + 0.12)) / 0.18) *
                    (1 - smooth((time - e.time) / 0.12));
                  const size =
                    orbit.id === "percussion"
                      ? 7
                      : 10 + (pitchMidi(e.pitch) % 12) * 0.36;
                  return (
                    <g key={e.id} opacity={opacity}>
                      {[-0.018, -0.036, -0.054].map((delay, j) => {
                        const tail = position(e, time + delay, r);
                        return (
                          <circle
                            key={delay}
                            cx={tail.x}
                            cy={tail.y}
                            r={size * (0.8 - j * 0.17)}
                            fill={orbit.color}
                            opacity={0.15 - j * 0.035}
                          />
                        );
                      })}
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={size + 9}
                        fill={orbit.color}
                        opacity=".10"
                      />
                      <circle cx={p.x} cy={p.y} r={size} fill={orbit.color} />
                      <circle
                        cx={p.x - 2}
                        cy={p.y - 3}
                        r={size * 0.33}
                        fill="#FFFDF7"
                        opacity={until < 0.2 ? 0.95 : 0.55}
                      />
                    </g>
                  );
                })}
              <circle
                cx="0"
                cy={-r}
                r={32 + pulse * 28}
                fill={`url(#light-${orbit.id})`}
                opacity={0.3 + 0.7 * pulse}
              />
              <rect
                x="-12"
                y={-r - 12}
                width="24"
                height="24"
                rx="7"
                transform={`rotate(45 0 ${-r})`}
                fill="#0B1324"
                stroke={orbit.color}
                strokeWidth="3"
              />
              <circle
                cx="0"
                cy={-r}
                r={3 + pulse * 7}
                fill="#FFF7E6"
                opacity={0.4 + pulse * 0.6}
              />
              {recent.map((e) => {
                const age = time - e.time,
                  life = Math.max(0, 1 - age / (orbit.release + 0.15));
                if (life === 0) return null;
                return (
                  <g key={e.id}>
                    <circle
                      cx="0"
                      cy={-r}
                      r={12 + age * (orbit.id === "bass" ? 100 : 66)}
                      fill="none"
                      stroke={orbit.color}
                      strokeWidth={orbit.id === "percussion" ? 3 : 2}
                      opacity={life * 0.8}
                    />
                    {orbit.id === "bell" &&
                      [-1, 1].map((sign) => (
                        <circle
                          key={sign}
                          cx={sign * (12 + age * 43)}
                          cy={-r - age * 42}
                          r={2.5 * life}
                          fill="#DFFAFF"
                          opacity={life}
                        />
                      ))}
                  </g>
                );
              })}
              {burst > 0 && (
                <circle
                  r={r + (1 - burst) * 60}
                  fill="none"
                  stroke={orbit.color}
                  strokeWidth={2 + burst * 3}
                  opacity={burst * 0.7}
                />
              )}
            </g>
          );
        })}
        <circle r={62 + active.length * 3 + ending * 25} fill="url(#core)" />
        <circle r="25" fill="#111C2F" stroke="#D6C6A1" strokeWidth="2" />
        <circle r="7" fill="#FFF0D1" />
      </g>
      <g transform="translate(520 1465)">
        {ORBITS.map((o, i) => {
          const w = entryWeight(o.id, time);
          const p = EVENTS.filter((e) => e.circleId === o.id).reduce(
            (m, e) => Math.max(m, reaction(e, time, o.release)),
            0,
          );
          return (
            <g
              key={o.id}
              opacity={0.18 + 0.82 * w}
              transform={`translate(${(i - 1.5) * 110} 0)`}
              color={o.color}
            >
              <circle
                r={36 + p * 6}
                fill={o.color}
                fillOpacity={0.035 + p * 0.1}
                stroke={o.color}
                strokeOpacity={0.12 + p * 0.3}
                strokeWidth="1.5"
              />
              <g
                transform={`translate(0 ${-p * 3}) scale(${0.85 + w * 0.15 + p * 0.06})`}
              >
                <InstrumentIcon instrument={o.id} />
              </g>
            </g>
          );
        })}
      </g>
      <rect
        width="1080"
        height="1920"
        fill="#070B18"
        opacity={smooth((time - (DURATION - 0.65)) / 0.65) * 0.3}
      />
      <OrbitEndCta time={time} />
    </svg>
  );
};
export const OrbitMusicBox: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return (
    <AbsoluteFill style={{ backgroundColor: "#070B18" }}>
      <Audio src={staticFile("orbit/music.wav")} />
      <OrbitArtwork time={frame / fps} />
    </AbsoluteFill>
  );
};
